import React from 'react';
import { MODE_SHAPES } from './modeIcons';

/**
 * Shared legend artwork.
 *
 * Every glyph here is the marker's own path, not a lookalike: the point of a
 * key is that it cannot describe something the map is not drawing. They live
 * in one module because three legends now render them — the combined All-mode
 * legend and the two single-layer ones — and three copies of a path is three
 * chances to drift.
 */

/**
 * The map's vessel triangle (ShipMarker's 24x24 glyph).
 *
 * The viewBox is cropped to the arrow rather than kept at the marker's
 * `0 0 24 24`, because the arrow occupies only 8x8 of that box — a third of
 * it. Sharing the marker's box made a 16px legend glyph draw a 5px arrow next
 * to air and truck, which fill their own 200x200 box almost completely. The
 * crop leaves the same ~90% fill they have, so all three rows read at one size.
 */
export const VesselGlyph = ({ className = '' }) => (
  <svg viewBox="7.5 1.5 9 9" className={className} aria-hidden="true">
    <path d="M12 2 L8 10 L12 8 L16 10 Z" fill="currentColor" />
  </svg>
);

/** Air / truck reuse the marker artwork verbatim so the key cannot drift. */
export const ModeGlyph = ({ mode, className = '' }) => (
  <svg viewBox="0 0 200 200" className={className} aria-hidden="true">
    {MODE_SHAPES[mode].map((s) => (
      <path key={s.d} d={s.d} fill="currentColor" />
    ))}
  </svg>
);

/**
 * The region pin's silhouette, at legend scale — the same path the map draws
 * (see growingPinIcon).
 *
 * The aspect is 12x16, not the 20 the Figma layer reports: that layer is a
 * 20x20 FRAME and the pin inside fills only 14x19 of a 24 box, so the drawn
 * pin is ~11.7x15.8. This viewBox is cropped to the artwork, so its numbers
 * are the pin itself — the same distinction that made the map pins oversized.
 */
export const PinGlyph = ({ color, isDark, className = 'h-[16px] w-[12px]' }) => (
  <svg viewBox="0 0 42.8 57.975" className={`shrink-0 ${className}`} aria-hidden="true">
    <path
      d="M21.4 0C9.6 0 0 9.6 0 21.4C0 36.6 18.2 55.5 19.9 57.3C20.7 58.2 22.1 58.2 22.9 57.3C24.6 55.5 42.8 36.6 42.8 21.4C42.8 9.6 33.2 0 21.4 0Z"
      fill={color}
    />
    {/* Same paper as the map's pins (growingPinIcon): a white dot here while
        the map draws #3D403E would make the key describe a pin that is not
        on screen. */}
    <circle cx="21.4" cy="21.4" r="7.5" fill={isDark ? '#3D403E' : '#fff'} />
  </svg>
);

/**
 * The collapsed pill's swatches — a hint at the panel's contents, not a key in
 * miniature. Colours are passed in from the live palettes so a change reaches
 * them without anyone remembering this file exists.
 */
export const SwatchStack = ({ colors }) => (
  <span className="relative ml-auto mr-[6px] block h-[6px] w-[14px] shrink-0" aria-hidden="true">
    {colors.map((color, i) => (
      <span
        key={color}
        className="absolute top-1/2 size-[6px] -translate-y-1/2 rounded-[3px] border border-solid border-white dark:border-[#1B1F1C]"
        style={{ left: i * 4, backgroundColor: color }}
      />
    ))}
  </span>
);
